import React, { useEffect, useState } from 'react';
import CustomModal from 'components/CustomModal/CustomModal';
import { IconValidateNew, IconClose } from 'components/Icons';
import { KSButton } from 'presentation/components/KSQ2/Common';
import './ActionDialog.scss';

interface ResolveDialogProps {
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (form: { comment: string }) => void;
  title?: string;
  commentLabel?: string;
  commentPlaceholder?: string;
  submitLabel?: string;
}

/**
 * Modal dialog for resolving/closing a request
 * Allows adding an optional closing comment
 */
export const ResolveDialog: React.FC<ResolveDialogProps> = ({
  isOpen,
  isPending,
  onClose,
  onSubmit,
  title = 'Resolve Request',
  commentLabel = 'Closing comment (optional)',
  commentPlaceholder = 'Add a closing comment to explain the resolution.',
  submitLabel = 'Resolve',
}) => {
  const [comment, setComment] = useState<string>('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setComment('');
      }, 300);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({ comment });
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="resolve-modal"
      shouldCloseOnEsc={!isPending}
      shouldCloseOnOverlayClick={!isPending}
    >
      <div className="resolve-modal__content">
        <h2 className="resolve-modal__title">{title}</h2>

        <div className="resolve-modal__form">
          <div className="resolve-modal__field">
            <label className="resolve-modal__label">{commentLabel}</label>
            <textarea
              className="resolve-modal__textarea"
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={commentPlaceholder}
              disabled={isPending}
              rows={4}
            />
          </div>
        </div>

        <div className="resolve-modal__actions">
          <KSButton variant="secondary" icon={<IconClose />} label="Cancel" onClick={onClose} disabled={isPending} />
          <KSButton
            variant="primary"
            icon={isPending ? <i className="fa fa-circle-o-notch fa-spin" /> : <IconValidateNew />}
            label={isPending ? 'Resolving...' : submitLabel}
            onClick={handleSubmit}
            disabled={isPending}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default ResolveDialog;
